import { useState, useEffect } from 'react';
import { api, fetchData } from '../services/api';
import { PATH_SEGMENTS } from '../config/paths';

interface KPILoadingOptions {
  interactionIds: (string | number)[];
  kpiNames: string[];
  reloadKey?: string | number | null;
  // Optional preloaded KPI data map: { [interactionId]: { [kpiName]: KpiData } }
  preloadedKpiMap?: Record<string, Record<string, any>>;
}

export const useKPILoading = ({ interactionIds, kpiNames, preloadedKpiMap, reloadKey }: KPILoadingOptions) => {
  const [kpiDataMap, setKpiDataMap] = useState<Record<string, any>>({});
  const [kpiLoading, setKpiLoading] = useState(false);
  const [kpiError, setKpiError] = useState<string | null>(null);

  useEffect(() => {
    const loadKpiData = async () => {
      // If no KPI names, nothing to do at all
      if (!kpiNames.length) {
        setKpiDataMap(preloadedKpiMap ? JSON.parse(JSON.stringify(preloadedKpiMap)) : {});
        setKpiLoading(false);
        return;
      }

      // If there are no interaction IDs to fetch for, still publish preloaded data (if any)
      if (!interactionIds.length) {
        setKpiDataMap(preloadedKpiMap ? JSON.parse(JSON.stringify(preloadedKpiMap)) : {});
        setKpiLoading(false);
        return;
      }
      console.log(`⭐ LOADING KPI DATA FOR INTERACTIONS:`, interactionIds);
      setKpiLoading(true);
      setKpiError(null);

      try {
        // Start new map with preloaded data (if any)
        const newKpiDataMap: Record<string, any> = preloadedKpiMap ? JSON.parse(JSON.stringify(preloadedKpiMap)) : {};
        console.log(`⭐ PRELOADED KPI DATA MAP:`, newKpiDataMap);

        // Determine which KPI calls are missing from preloaded data
        const apiCalls: Array<{ interactionId: string | number; kpiName: string; promise: Promise<any> }> = [];
        interactionIds.forEach(interactionId => {
          const idStr = String(interactionId);
          kpiNames.forEach(kpiName => {
            const hasPreloaded = newKpiDataMap[idStr] && newKpiDataMap[idStr][kpiName] !== undefined && newKpiDataMap[idStr][kpiName] !== null;
            if (!hasPreloaded) {
              apiCalls.push({
                interactionId,
                kpiName,
                promise: fetchData(`${api(PATH_SEGMENTS.KPI, kpiName)}/?iid=${interactionId}`)
              });
            }
          });
        });

        // If everything is preloaded, short-circuit without any fetch
        if (apiCalls.length === 0) {
          console.log(`⭐ ALL KPI DATA IS PRELOADED, NO FETCHES NEEDED`);
          setKpiDataMap(newKpiDataMap);
          setKpiLoading(false);
          return;
        }

        // Use Promise.allSettled to handle individual API failures gracefully
        const results = await Promise.allSettled(apiCalls.map(call => call.promise));
        
        results.forEach((result, index) => {
          const { interactionId, kpiName } = apiCalls[index];
          const interactionIdStr = String(interactionId);
          
          if (!newKpiDataMap[interactionIdStr]) {
            newKpiDataMap[interactionIdStr] = {};
          }

          if (result.status === 'fulfilled') {
            const parsedData = typeof result.value === 'string' ? JSON.parse(result.value) : result.value;
            newKpiDataMap[interactionIdStr][kpiName] = parsedData;
          } else {
            console.error(`Failed to fetch KPI '${kpiName}' for interaction ${interactionId}:`, result.reason);
            if (newKpiDataMap[interactionIdStr][kpiName] === undefined) {
              newKpiDataMap[interactionIdStr][kpiName] = null;
            }
          }
        });

        setKpiDataMap(newKpiDataMap);

        // Only set error if all requests (that we attempted) failed
        const failedCount = results.filter(result => result.status === 'rejected').length;
        if (apiCalls.length > 0 && failedCount === apiCalls.length) {
          setKpiError("Failed to load KPI data");
        } else if (failedCount > 0) {
          console.warn(`${failedCount} KPI requests failed, but others succeeded`);
        }
      } catch (error) {
        console.error("Error in KPI loading:", error);
        setKpiError("Failed to load KPI data");
      } finally {
        setKpiLoading(false);
      }
    };

    loadKpiData();
  }, [interactionIds.join(','), kpiNames.join(','), JSON.stringify(preloadedKpiMap || {}), reloadKey]); // include preloaded map so we recompute when present

  // Helper function to get KPI data for a specific interaction
  const getKpiData = (interactionId: string | number, kpiName: string): any => {
    const interactionIdStr = String(interactionId);
    return kpiDataMap[interactionIdStr]?.[kpiName] || null;
  };

  return {
    kpiDataMap,
    kpiLoading,
    kpiError,
    getKpiData
  };
}; 